<template>
  <nav class="navbar navbar-expand-lg navbar-dark bg-primary sticky-top">
    <div class="container">
      <!-- Brand -->
      <router-link class="navbar-brand fw-bold" to="/">
        {{ $t('app.title') }}
      </router-link>

      <!-- Mobile toggle -->
      <button
        class="navbar-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#navbarNav"
        aria-controls="navbarNav"
        aria-expanded="false"
        :aria-label="$t('common.close')"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <!-- Nav items -->
      <div class="collapse navbar-collapse" id="navbarNav">
        <ul class="navbar-nav me-auto">
          <li class="nav-item">
            <router-link class="nav-link" to="/" exact-active-class="active">
              {{ $t('nav.home') }}
            </router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link" to="/calendar" active-class="active">
              <i class="bi bi-calendar3 me-1"></i>
              {{ $t('nav.calendar') }}
            </router-link>
          </li>
          <!-- Admin menu (Story 3.1) -->
          <li v-if="authStore.hasPermission('events.view')" class="nav-item dropdown">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              id="adminDropdown"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i class="bi bi-gear me-1"></i>
              {{ $t('admin.nav.admin') }}
            </a>
            <ul class="dropdown-menu" aria-labelledby="adminDropdown">
              <li>
                <router-link class="dropdown-item" to="/admin/events">
                  <i class="bi bi-calendar-event me-2"></i>
                  {{ $t('admin.nav.events') }}
                </router-link>
              </li>
            </ul>
          </li>
        </ul>

        <!-- User dropdown -->
        <ul v-if="authStore.isAuthenticated" class="navbar-nav">
          <li class="nav-item dropdown">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              id="userDropdown"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span class="d-none d-md-inline me-1">{{ authStore.fullName }}</span>
              <span class="badge bg-light text-primary">{{ authStore.user?.licence_number }}</span>
            </a>
            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
              <li>
                <span class="dropdown-item-text text-muted small">
                  {{ authStore.user?.role_label || roleLabel }}
                </span>
              </li>
              <li v-if="authStore.user?.club">
                <span class="dropdown-item-text text-muted small">
                  {{ authStore.user.club.name }}
                </span>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <router-link class="dropdown-item" to="/profile">
                  <i class="bi bi-person me-2"></i>
                  {{ $t('nav.profile') }}
                </router-link>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li class="dropdown-item-text">
                <AccessibilitySettings />
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <button
                  class="dropdown-item text-danger"
                  @click="handleLogout"
                  :disabled="isLoggingOut"
                >
                  <span v-if="isLoggingOut" class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">{{ $t('common.loading') }}</span>
                  </span>
                  {{ $t('auth.logout') }}
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>

  <!-- Toast for logout confirmation -->
  <div
    class="toast-container position-fixed bottom-0 end-0 p-3"
    style="z-index: 1055"
  >
    <div
      ref="toastEl"
      class="toast align-items-center text-bg-success border-0"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div class="d-flex">
        <div class="toast-body">
          {{ $t('auth.logout_success') }}
        </div>
        <button
          type="button"
          class="btn-close btn-close-white me-2 m-auto"
          data-bs-dismiss="toast"
          :aria-label="$t('common.close')"
        ></button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { Toast } from 'bootstrap';
import AccessibilitySettings from '@/components/AccessibilitySettings.vue';

const router = useRouter();
const authStore = useAuthStore();

const isLoggingOut = ref(false);
const toastEl = ref<HTMLElement | null>(null);
let toastInstance: Toast | null = null;

// Role label fallback if not provided by API
const roleLabel = computed(() => {
  const roleLabels: Record<string, string> = {
    licencie: 'Licencié',
    responsable_club: 'Responsable Club',
    responsable_sportif: 'Responsable Sportif',
    admin: 'Administrateur',
  };
  return roleLabels[authStore.user?.role ?? 'licencie'];
});

onMounted(() => {
  if (toastEl.value) {
    toastInstance = new Toast(toastEl.value, { autohide: true, delay: 3000 });
  }
});

/**
 * Handle logout with confirmation toast
 */
async function handleLogout(): Promise<void> {
  isLoggingOut.value = true;

  await authStore.logout();

  isLoggingOut.value = false;

  // Show success toast briefly before redirect
  if (toastInstance) {
    toastInstance.show();
  }

  // Redirect to login after short delay
  setTimeout(() => {
    router.push({ name: 'login' });
  }, 500);
}
</script>

<style scoped>
.navbar-brand {
  letter-spacing: -0.5px;
}

.dropdown-item-text {
  padding: 0.25rem 1rem;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
}
</style>
